/**
 * AuthProvider: single source of truth for Entra (Azure AD) authentication.
 *
 * Owns MSAL initialization, exposes the current session (isAuthenticated, user),
 * init error state, and the auth actions (login, logout, getApiToken) via context.
 * Wraps the app above AuthGuard so the guard and all consumers read one snapshot
 * of auth state rather than calling the imperative auth helpers ad hoc.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { User } from "../types/index";
import { env, IS_DEV } from "./env";
import { reportError, setUser } from "./newrelic";
import {
  getApiToken as getApiTokenFn,
  getUser,
  getUser as getUserFn,
  initializeAuth,
  isAuthenticated as isAuthenticatedFn,
  login as loginFn,
  logout as logoutFn,
} from "./auth";

// ── Session snapshot ─────────────────────────────────────────────────────────
// isAuthenticated()/getUser() read MSAL synchronously. We snapshot them into
// state so the context value is referentially stable between renders (getUser()
// returns a fresh object each call, which would otherwise defeat memoization).

interface Session {
  isAuthenticated: boolean;
  user: User | null;
}

const EMPTY_SESSION: Session = { isAuthenticated: false, user: null };

function readSession(): Session {
  return { isAuthenticated: isAuthenticatedFn(), user: getUserFn() };
}

// ── Context ──────────────────────────────────────────────────────────────────

interface AuthContextValue {
  /** True once MSAL init + redirect handling has completed (or was skipped in dev). */
  initialized: boolean;
  /** Whether a real MSAL session exists. Does not account for the dev bypass — see skipAuth. */
  isAuthenticated: boolean;
  /** The signed-in Entra user, or null when signed out. */
  user: User | null;
  /** Non-null when MSAL initialization failed. */
  error: string | null;
  /** Dev-only bypass: true in dev when no Entra client is configured, so the login gate is skipped. */
  skipAuth: boolean;
  /** Begin the Entra login redirect. Navigates away on success. */
  login: () => Promise<void>;
  /** Begin the Entra logout redirect. Navigates away on success. */
  logout: () => Promise<void>;
  /** Acquire an API access token scoped to the marketplace API. Null when signed out. */
  getApiToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ───────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  // In dev without Entra config, skip MSAL entirely so the app is usable locally.
  // (import.meta.env can't be read under ts-jest, so go through the env module.)
  const skipAuth = IS_DEV && !env.REACT_APP_ENTRA_CLIENT_ID;

  const [initialized, setInitialized] = useState(skipAuth);
  const [error, setError] = useState<string | null>(null);
  // Read the session eagerly so the first render reflects an existing session
  // (e.g. after a redirect round-trip) without waiting for the init effect.
  const [session, setSession] = useState<Session>(() =>
    skipAuth ? EMPTY_SESSION : readSession(),
  );

  useEffect(() => {
    if (skipAuth) return;
    initializeAuth()
      .then(() => {
        setInitialized(true);
        setSession(readSession());
        const user = getUser();
        if (user) {
            setUser(user.id);
        }
      })
      .catch((err) => {
        console.error("Auth initialization failed:", err);
        reportError(err, "auth-init");
        setError(err instanceof Error ? err.message : String(err));
        setInitialized(true);
      });
  }, [skipAuth]);

  const login = useCallback(() => loginFn(), []);
  const logout = useCallback(() => logoutFn(), []);
  const getApiToken = useCallback(() => getApiTokenFn(), []);

  const value = useMemo<AuthContextValue>(
    () => ({
      initialized,
      isAuthenticated: session.isAuthenticated,
      user: session.user,
      error,
      skipAuth,
      login,
      logout,
      getApiToken,
    }),
    [initialized, session, error, skipAuth, login, logout, getApiToken],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return ctx;
}
